import React from 'react';
import {ArrowLeft, Compass, FolderKanban, GraduationCap, House, NotebookPen} from 'lucide-react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {useAuth} from '../authentication/AuthContext';
import Header from '../components/Header';

const authenticatedDestinations = [
    {to: '/projects', label: 'Projects', icon: FolderKanban},
    {to: '/notes', label: 'Notes', icon: NotebookPen},
    {to: '/practice', label: 'Practice', icon: GraduationCap},
];

function NotFoundPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const {user} = useAuth();
    const homePath = user ? '/home' : '/';

    return (
        <div className="min-h-screen bg-[var(--workspace-bg)] text-[var(--text-primary)]">
            <Header/>

            <main className="relative flex min-h-screen items-center justify-center overflow-hidden px-4 pb-10 pt-24">
                <div className="pointer-events-none absolute left-1/2 top-1/2 h-80 w-80 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--brand-purple)]/10 blur-3xl"/>

                <section className="relative w-full max-w-2xl rounded-3xl border border-[var(--brand-lavender)]/35 bg-[var(--brand-cream)]/90 p-6 text-center shadow-[0_24px_70px_var(--brand-purple-shadow)] backdrop-blur-sm sm:p-10">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-[var(--brand-lavender)]/40 bg-[var(--brand-purple)]/10 text-[var(--brand-purple)]">
                        <Compass size={30} aria-hidden="true"/>
                    </div>

                    <p className="mt-6 text-sm font-semibold uppercase tracking-[0.28em] text-[var(--brand-coral)]">Error 404</p>
                    <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">This page wandered off.</h1>
                    <p className="mx-auto mt-3 max-w-lg text-sm leading-6 text-[var(--text-muted)] sm:text-base">
                        The address may be incorrect, or the page may have been moved. Your work is safe—let’s get you back on track.
                    </p>

                    <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="inline-flex items-center justify-center gap-2 rounded-xl border border-[var(--brand-lavender)]/50 px-5 py-2.5 text-sm font-semibold transition hover:bg-[var(--brand-purple)]/10"
                        >
                            <ArrowLeft size={17} aria-hidden="true"/>
                            Go back
                        </button>
                        <Link
                            to={homePath}
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--brand-purple)] px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-[var(--brand-purple-shadow)] transition hover:brightness-110"
                        >
                            <House size={17} aria-hidden="true"/>
                            {user ? 'Go to home' : 'Go to homepage'}
                        </Link>
                    </div>

                    {user && (
                        <div className="mt-8 border-t border-[var(--brand-lavender)]/30 pt-6">
                            <p className="mb-3 text-xs font-medium uppercase tracking-wider text-[var(--text-muted)]">Or continue to</p>
                            <div className="grid gap-2 sm:grid-cols-3">
                                {authenticatedDestinations.map(({to, label, icon: Icon}) => (
                                    <Link
                                        key={to}
                                        to={to}
                                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-[var(--brand-lavender)]/30 bg-[var(--workspace-bg)]/60 px-3 py-2.5 text-sm font-medium transition hover:border-[var(--brand-purple)]/40 hover:text-[var(--brand-purple)]"
                                    >
                                        <Icon size={16} aria-hidden="true"/>
                                        {label}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    <p className="mt-6 truncate text-xs text-[var(--text-muted)]" title={location.pathname}>
                        Could not find: {location.pathname}
                    </p>
                </section>
            </main>
        </div>
    );
}

export default NotFoundPage;
